import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button } from '..';
import {
  generateBulkThreatExplanation,
  BulkThreatExplainPayload,
  ThreatExplainResponse,
} from '../../services/threatExplainService';
import {
  logBulkGeneration,
  BulkExplainSummary,
  GenerationSource,
} from '../../services/threatExplainPersistence';
import { readEmailFromLocalStorage } from './explainOptions';
import { useCurrentUserQuery } from '../../queries/user';

interface BulkExplainSectionProps {
  source: Exclude<GenerationSource, 'single'>;
  // null = the surrounding form doesn't have enough input yet (no domains).
  payload: BulkThreatExplainPayload | null;
  protectedAssetId: number | null;
  companyId: number | null;
  // Fired with the summary to attach to every created threat (re-fired on
  // every edit keystroke with the current text), or null when cleared
  // (regenerate in flight). The parent persists it post-creation.
  onSummaryChange: (summary: BulkExplainSummary | null) => void;
}

/**
 * AI narrative for a batch of threats being created (WHEEL-353: single model,
 * editable before create). The text shown in the textarea is exactly what
 * gets attached to every created threat; edits flip its origin to 'edit'.
 */
const BulkExplainSection: React.FC<BulkExplainSectionProps> = ({
  source,
  payload,
  protectedAssetId,
  companyId,
  onSummaryChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ThreatExplainResponse | null>(null);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [text, setText] = useState('');

  const { data: currentUser } = useCurrentUserQuery();
  const internalUserId: string | null = currentUser?.Id ?? null;
  const userEmail = readEmailFromLocalStorage();

  const canGenerate =
    !!payload &&
    payload.domains.length > 0 &&
    !!internalUserId &&
    !!userEmail &&
    !loading;

  // One domain = a single attack: the edge function switches to the
  // single-threat prompt, and the copy should not talk about campaigns.
  const isSingleDomain = payload?.domains.length === 1;

  const emitSummary = (
    nextText: string,
    res: ThreatExplainResponse,
    genId: string,
  ) => {
    if (!internalUserId || !userEmail) return;
    const trimmed = nextText.trim();
    if (!trimmed) {
      onSummaryChange(null);
      return;
    }
    onSummaryChange({
      generation_id: genId,
      text: trimmed,
      origin: trimmed === res.summary.explanation.trim() ? 'ai' : 'edit',
      prompt_version: res.prompt_version,
      user_id: internalUserId,
      user_email: userEmail,
    });
  };

  const handleGenerate = async () => {
    if (!payload || !internalUserId || !userEmail) return;
    setLoading(true);
    setError(null);
    setGenerationId(null);
    onSummaryChange(null);
    try {
      const res = await generateBulkThreatExplanation(payload);
      setResult(res);
      setText(res.summary.explanation);
      try {
        const id = await logBulkGeneration({
          source,
          protected_asset_id: protectedAssetId,
          company_id: companyId,
          user_id: internalUserId,
          user_email: userEmail,
          prompt_version: res.prompt_version,
          payload,
          summary: res.summary,
        });
        setGenerationId(id);
        emitSummary(res.summary.explanation, res, id);
      } catch (logErr) {
        // Non-fatal: keep the narrative on screen, but without a generation id
        // it can't be attached to created threats.
        console.error('[threat-explain] log bulk generation failed', logErr);
        setError(
          'Failed to log the generation — the summary will not be saved to created threats.',
        );
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (next: string) => {
    setText(next);
    if (result && generationId) emitSummary(next, result, generationId);
  };

  const edited = !!result && text.trim() !== result.summary.explanation.trim();

  return (
    <Accordion disableGutters data-testid="bulk-explain-section" sx={{ mt: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        data-testid="bulk-explain-toggle"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">
            {isSingleDomain ? 'AI summary' : 'AI campaign summary'}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {isSingleDomain
              ? 'Generate an explanation for this threat from the detection ' +
                'signals and selected notes. You can edit the text before ' +
                'creating — it is saved to the created threat.'
              : 'Generate one campaign-level explanation for all domains in ' +
                'this batch. You can edit the text before creating — it is ' +
                'saved to every threat in this batch.'}
          </Typography>
          <Button
            variant="contained"
            size="small"
            data-testid="bulk-explain-generate"
            onClick={handleGenerate}
            disabled={!canGenerate}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
          >
            {result ? 'Regenerate' : 'Generate'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        {result && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
                flexWrap: 'wrap',
              }}
            >
              <Chip
                icon={
                  edited ? (
                    <EditIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                  )
                }
                label={edited ? 'Edited' : 'AI generated'}
                size="small"
                color={edited ? 'secondary' : 'primary'}
                variant="outlined"
                sx={{ height: 22, fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                Prompt: {result.prompt_version} · Generated{' '}
                {new Date(result.generated_at).toLocaleString()}
              </Typography>
            </Box>
            <TextField
              multiline
              fullWidth
              minRows={4}
              value={text}
              onChange={e => handleTextChange(e.target.value)}
              inputProps={{ 'data-testid': 'bulk-explain-text-input' }}
              sx={{ mt: 1 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {isSingleDomain
                ? 'This text will be saved to the created threat.'
                : 'This text will be saved to every threat in this batch.'}
            </Typography>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default BulkExplainSection;
